<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="modelValue" class="modal-overlay" @click.self="closeDialog">
        <div class="modal-container" :class="`modal-${size}`">
          <!-- Modal Header -->
          <div class="modal-header">
            <h3 class="modal-title">{{ title }}</h3>
            <button class="modal-close" @click="closeDialog">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </button>
          </div>

          <!-- Modal Body (Dynamic Content Slot) -->
          <div class="modal-body">
            <slot></slot>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer" v-if="showFooter">
            <button class="button button-cancel" @click="closeDialog">{{ cancelText }}</button>
            <button v-if="hasSubmit" class="button button-save" @click="handleSubmit" :disabled="loading">
              <span v-if="loading" class="button-loader"></span>
              <span v-else>{{ submitText }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'Modal',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
      default: false
    },
    title: {
      type: String,
      required: true,
      default: 'Modal Title'
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large', 'xlarge', 'fullpage'].includes(value)
    },
    hasSubmit: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    submitText: {
      type: String,
      default: 'Save'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    }
  },

  emits: ['update:modelValue', 'submit', 'close'],

  methods: {
    closeDialog() {
      this.$emit('update:modelValue', false);
      this.$emit('close');
    },
    handleSubmit() {
      this.$emit('submit');
    }
  }
};
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 1.25rem;
  backdrop-filter: blur(2px);
}

/* Modal Container */
.modal-container {
  background: #ffffff;
  border-radius: 0.75rem;
  width: 100%;
  box-shadow: 0 0.625rem 2.5rem rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

/* Modal Sizes */
.modal-small {
  max-width: 25rem;
}

.modal-medium {
  max-width: 31.25rem;
}

.modal-large {
  max-width: 43.75rem;
}

.modal-xlarge {
  max-width: 56.25rem;
}

/* Full Page Modal */
.modal-fullpage {
  max-width: calc(100vw - 2.5rem);
  max-height: calc(100vh - 2.5rem);
  height: calc(100vh - 2.5rem);
}

.modal-fullpage .modal-body {
  max-height: none;
}

/* Modal Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 1.25rem;
  border-bottom: 0.0625rem solid #e5e5e5;
  flex-shrink: 0;
}

.modal-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.4;
}

.modal-close {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  color: #666666;
  transition: color 0.2s ease, background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  flex-shrink: 0;
}

.modal-close:hover {
  color: #1a1a1a;
  background-color: #f5f5f5;
}

.modal-close:focus {
  outline: 0.125rem solid #0c8188;
  outline-offset: 0.125rem;
}

/* Modal Body */
.modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
  flex: 1;
}

.modal-body::-webkit-scrollbar {
  width: 0.5rem;
}

.modal-body::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #cccccc;
  border-radius: 0.25rem;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #999999;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem 1.5rem;
  border-top: 0.0625rem solid #e5e5e5;
  flex-shrink: 0;
}

/* Buttons */
.button {
  border: 0.0625rem solid #0c8188;
  border-radius: 0.625rem;
  padding: 6px 25px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  line-height: 1.5;
}

.button:focus {
  outline: 0.125rem solid #0c8188;
  outline-offset: 0.125rem;
}

.button-cancel {
  color: #0c758d;
  background: #ffffff;
}

.button-cancel:hover {
  background-color: #f5f5f5;
}

.button-cancel:active {
  background-color: #ebebeb;
}

.button-save {
  background: #0c8188;
  color: #ffffff;
  border-color: #0c8188;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 5rem;
}

.button-save:hover:not(:disabled) {
  background-color: #09676d;
  border-color: #09676d;
}

.button-save:active:not(:disabled) {
  background-color: #064c51;
  border-color: #064c51;
}

.button-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Button Loader */
.button-loader {
  width: 1rem;
  height: 1rem;
  border: 0.125rem solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
  transition: transform 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-container,
.modal-fade-leave-to .modal-container {
  transform: scale(0.95);
}

/* Responsive */
@media (max-width: 37.5rem) {
  .modal-container {
    max-width: 100% !important;
    margin: 0 1rem;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>